import type { IStandardErrorResponse } from 'src/api/common';
import { useMutation, useQueryClient } from 'react-query';

import type { IPlaybook, IUpdatePlaybookInput } from 'src/api/playbooks-api';
import type { IPlaybookV2 } from 'src/services/api/playbooks/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { playbooksPath } from 'src/services/api/playbooks/path';
import { getApiErrorMessage } from 'src/services/api/utils';
import { ELIGIBLE_ROWS_QUERY_KEY } from 'src/services/api/playbooks/index';

export type IUpdatePlaybookRequest = IUpdatePlaybookInput;

export const useUpdatePlaybook = (uuid?: string) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<IPlaybook, IStandardErrorResponse, IUpdatePlaybookRequest>(
        async (payload) => {
            const response = await apiClient(`${playbooksPath}/${uuid}`, {
                method: 'patch',
                json: {
                    ...payload,
                    trigger_params: {
                        ...payload.trigger_params,
                        run_limit_per_lead: payload.trigger_params?.run_limit_per_lead || 1
                    }
                }
            }).json<IPlaybook>();

            return response;
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.playbooks);
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(queryKeys.teamPlaybooks);
                queryClient.invalidateQueries(`${ELIGIBLE_ROWS_QUERY_KEY}-${uuid}`);
                // Toast fires here (step 2) so it's suppressed if duplicate validation fails.
                addNotification('Playbook updated', 'success');
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update playbook';
                addNotification(errorMessage, 'error');
            }
        }
    );
};

type IUpdatePlaybookV2Request = Pick<IPlaybookV2, 'first_step'>;

export const useUpdatePlaybookV2 = (uuid?: string) => {
    // const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<IPlaybookV2, IStandardErrorResponse, IUpdatePlaybookV2Request>(
        async (payload) => {
            const res = await apiClient(`api/landlord/playbooks/${uuid}/steps`, {
                method: 'put',
                json: payload
            }).json<IPlaybookV2>();

            return res;
        }, {
            onSuccess: () => {
                // Don't invalidate the queries here because we have to update the playbook title separately
                // And, we'll invalidate the queries in the `useUpdatePlaybook` hook.
                // queryClient.invalidateQueries(queryKeys.playbooks);
                // queryClient.invalidateQueries(queryKeys.playbooksV2);
                // queryClient.invalidateQueries(queryKeys.teamPlaybooks);
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update playbook';
                addNotification(errorMessage, 'error');
            }
        }
    );
};
